import { usePathname } from 'next/navigation';
import usePost from './usePost';
import { toast } from 'react-hot-toast';
import { useCartStore } from '@/store/useCartStore';
import { useState } from 'react';
import { trackFbAndCAPI, pushDataLayer, metaId } from '@/lib/fbPixel';


interface CartItemLoadingState {
  [productId: string]: boolean;
}

const useCart = () => {
  const { cart, setCart } = useCartStore();
  const { PostMethod } = usePost();
  const [cartLoading, setCartLoading] = useState<CartItemLoadingState>({});
  const pathname = usePathname();

  const getCartList = async () => {
    try {
      let response = await PostMethod({
        url: 'cart/get-cart',
        method: 'get',
        language: pathname.split('/')[1],
      });

      if (response?.success === true) {
        setCart(response.data);
      } else {
        setCart(null);
      }
    } catch {
      // silent
    }
  };

  const setProductLoading = (productId: string, isLoading: boolean) => {
    setCartLoading((prev) => ({ ...prev, [productId]: isLoading }));
  };

  const addtCartList = async (
    productId: string,
    qty = 1,
    meta?: { price?: number; name?: string; brand?: string; category?: string; barcode?: string },
  ) => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: 'cart/add-cart',
        body: { productId, qty },
        method: 'post',
      });

      if (response?.success === true) {
        await getCartList();
        toast.success(response?.message);
        const value = meta?.price != null ? Number(meta.price) * qty : undefined;
        const pixelId = metaId(meta?.barcode, productId);
        trackFbAndCAPI('AddToCart', {
          content_ids: [pixelId],
          ...(meta?.name && { content_name: meta.name }),
          ...(meta?.category && { content_category: meta.category }),
          content_type: 'product',
          currency: 'KWD',
          ...(value != null && { value }),
          contents: [{ id: pixelId, quantity: qty, ...(meta?.price != null && { item_price: meta.price }) }],
        });
        pushDataLayer('add_to_cart', {
          ecommerce: {
            currency: 'KWD',
            ...(value != null && { value }),
            items: [
              {
                item_id: productId,
                ...(meta?.name && { item_name: meta.name }),
                ...(meta?.brand && { item_brand: meta.brand }),
                ...(meta?.price != null && { price: meta.price }),
                quantity: qty,
              },
            ],
          },
        });
      } else {
        toast.error(response?.message || 'Failed to add to cart.');
      }
    } catch {
      // silent
    } finally {
      setProductLoading(productId, false);
    }
  };

  const removeCartList = async (productId: string) => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: 'cart/remove-cart',
        body: { productId },
        method: 'put',
      });

      if (response?.success === true) {
        await getCartList();
        toast.success(response?.message);
      } else {
        toast.error(response?.message || 'Failed to remove from cart.');
      }
    } catch (error) {
      toast.error('An error occurred while removing from the cart.');
    } finally {
      setProductLoading(productId, false);
    }
  };

  const setCartQuantity = async (productId: string, quantity: number): Promise<{ success: boolean; message: string }> => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: 'cart/set-quantity',
        body: { productId, quantity },
        method: 'post',
      });

      if (response?.success === true) {
        await getCartList();
        return { success: true, message: response?.message || 'Quantity updated' };
      } else {
        return { success: false, message: response?.message || 'Failed to update quantity' };
      }
    } catch (error) {
      console.error('Error setting cart quantity:', error);
      return { success: false, message: 'Failed to update quantity' };
    } finally {
      setProductLoading(productId, false);
    }
  };

  const deleteCartList = async (productId: string) => {
    setProductLoading(productId, true);
    try {
      let response = await PostMethod({
        url: 'cart/delete-cart',
        body: { productId },
        method: 'put',
      });

      if (response?.success === true) {
        await getCartList();
        toast.success(response?.message);
      } else {
        toast.error(response?.message || 'Failed to delete from cart.');
      }
    } catch (error) {
      console.error('Error deleting from cart:', error);
      toast.error('An error occurred while deleting from the cart.');
    } finally {
      setProductLoading(productId, false);
    }
  };

  return {
    cartLoading,
    cart,
    deleteCartList,
    getCartList,
    addtCartList,
    removeCartList,
    setCartQuantity
  };
};

export default useCart;
